import { createPortal } from 'react-dom';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import ReviewModal, { ReviewModalFloatingSlotContext } from '../../layouts/ReviewModal';
import ActionBar from '../../components/ActionBar';
import NoteApplyingSkeleton from '../../components/review/NoteApplyingSkeleton';
import { VitalsCardGrid } from '../../components/review/VitalCard';
import MedicationCard from '../../components/review/MedicationCard';
import { HoldToRecordButton } from '../../contexts/HoldToRecordContext';
import { REVIEW_PAGE_BG, reviewCardClass } from '../../components/review/reviewStyles';
import { parseBp, stripO2SatPercent } from '../../utils/vitalsParse';
import { doctorPatientPath, ROUTES } from '../../constants/routes';
import {
  doctorCreateNote,
  doctorGetNote,
  doctorMarkDocumentDownloaded,
} from '../services/doctorApi';
import {
  generateDoctorDocumentPDF,
  getDoctorDocumentTitle,
} from '../services/doctorDocumentPdf';
import { updateDoctorNoteFromCorrection } from '../services/doctorNotePipeline';
import { useDoctorSession } from '../contexts/DoctorSessionContext';

function patientInitials(name = '') {
  return String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

const NOTE_TYPE_LABELS = {
  first_visit: 'First Visit',
  follow_up: 'Follow-up',
  procedure: 'Procedure',
  emergency: 'Emergency',
};

const GRAY_TEXTAREA =
  'w-full resize-y rounded-xl border-0 bg-gray-100 px-3 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-70';

function setPath(obj, path, value) {
  const next = structuredClone(obj || {});
  const parts = path.split('.');
  let cur = next;
  for (let i = 0; i < parts.length - 1; i += 1) {
    if (!cur[parts[i]] || typeof cur[parts[i]] !== 'object') cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return next;
}

function doctorVitalsToNurseNote(vitals = {}) {
  const { systolic, diastolic } = parseBp(vitals.bp);
  const num = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? v : n;
  };
  return {
    vitals: {
      bp_systolic: systolic,
      bp_diastolic: diastolic,
      pulse: num(vitals.hr ?? vitals.pulse),
      temp_celsius: num(vitals.temp ?? vitals.temp_celsius),
      o2_sat: num(stripO2SatPercent(vitals.o2_sat)),
      weight_kg: num(vitals.weight ?? vitals.weight_kg),
      rr: num(vitals.rr),
      pain: num(vitals.pain),
      blood_sugar: num(vitals.blood_sugar),
    },
  };
}

function nurseNoteToDoctorVitals(vitalsNote) {
  const v = vitalsNote?.vitals || {};
  const bp =
    v.bp_systolic != null && v.bp_diastolic != null
      ? `${v.bp_systolic}/${v.bp_diastolic}`
      : v.bp_systolic != null
        ? String(v.bp_systolic)
        : '';
  return {
    bp,
    hr: v.pulse != null ? String(v.pulse) : '',
    temp: v.temp_celsius != null ? String(v.temp_celsius) : '',
    o2_sat: v.o2_sat != null ? String(stripO2SatPercent(v.o2_sat)) : '',
    weight: v.weight_kg != null ? String(v.weight_kg) : '',
    rr: v.rr != null ? String(v.rr) : '',
    pain: v.pain != null ? String(v.pain) : '',
  };
}

function hasVitals(vitals) {
  if (!vitals || typeof vitals !== 'object') return false;
  return Object.values(vitals).some((v) => v != null && String(v).trim() !== '');
}

function toMedCard(med) {
  return {
    ...med,
    drug_name: med.drug_name || med.name || '',
    dose: med.dose || '',
    route: med.route || 'oral',
  };
}

function fromMedCard(med) {
  return {
    ...med,
    name: med.name || med.drug_name || '',
    dose: med.dose || '',
    route: med.route || 'oral',
    frequency: med.frequency || '',
    duration: med.duration || '',
    instructions: med.instructions || '',
    high_alert: Boolean(med.high_alert),
    allergy_conflict: Boolean(med.allergy_conflict),
    allergy_conflict_reason: med.allergy_conflict_reason || null,
  };
}

function SoapGroupCard({ letter, title, children, hide }) {
  if (hide) return null;
  return (
    <div className={reviewCardClass('normal', 'w-full')}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
          {letter}
        </span>
        <span className="flex-1 text-right text-sm font-semibold text-gray-700">{title}</span>
      </div>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

function GrayField({ label, value, onChange, readOnly, hideIfEmpty, danger, rows = 2 }) {
  const text = value || '';
  if (hideIfEmpty && !text && readOnly) return null;

  return (
    <div>
      {label ? (
        <p
          className={`mb-1.5 text-[11px] font-semibold uppercase tracking-wide ${
            danger && text ? 'text-red-600' : 'text-gray-500'
          }`}
        >
          {label}
        </p>
      ) : null}
      <textarea
        value={text}
        readOnly={readOnly}
        disabled={readOnly}
        rows={rows}
        onChange={(e) => onChange?.(e.target.value)}
        className={`${GRAY_TEXTAREA} ${danger && text ? 'text-red-700' : ''}`}
        dir="auto"
      />
    </div>
  );
}

function MedList({ meds, readOnly, onChangeAt, onDeleteAt }) {
  if (!meds?.length) return null;
  return (
    <div className="flex flex-col gap-2.5">
      {meds.map((med, index) => (
        <MedicationCard
          key={`${med.name || med.drug_name}-${index}`}
          med={med}
          allowDelete={!readOnly}
          onChange={(next) => onChangeAt?.(index, next)}
          onDelete={() => onDeleteAt?.(index)}
        />
      ))}
    </div>
  );
}

function DocumentsSection({
  documents,
  documentsDetected,
  patientName,
  doctorName,
  clinicName,
  clinicLogoUrl,
}) {
  const rows = documents?.length
    ? documents.map((d) => ({
        id: d.id,
        type: d.document_type,
        data: d.document_json,
      }))
    : Object.entries(documentsDetected || {})
        .filter(([, v]) => v?.needed)
        .map(([type, data]) => ({ id: null, type, data }));

  if (!rows.length) return null;

  const downloadPdf = async (row) => {
    generateDoctorDocumentPDF({
      type: row.type,
      data: row.data,
      patientName,
      doctorName,
      clinicName,
      clinicLogoUrl,
    });
    if (row.id) {
      try {
        await doctorMarkDocumentDownloaded(row.id);
      } catch {
        // ignore
      }
    }
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <FileText className="h-5 w-5 shrink-0 text-primary" strokeWidth={2} />
        <h3 className="text-base font-bold text-gray-900">Generated Documents</h3>
      </div>
      {rows.map((row) => {
        const title = getDoctorDocumentTitle(row.type);
        const urgency = row.data?.urgency;
        return (
          <div key={`${row.type}-${row.id || 'new'}`} className={reviewCardClass('normal', 'w-full')}>
            <div className="mb-2 flex items-center gap-2">
              <p className="flex-1 text-sm font-semibold text-gray-800">{title}</p>
              {urgency ? (
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    urgency === 'urgent' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {urgency}
                </span>
              ) : null}
            </div>
            {row.type === 'sick_leave' && (
              <p className="text-sm text-gray-600">
                {row.data.diagnosis} · {row.data.days} days
              </p>
            )}
            {row.type === 'referral' && (
              <p className="text-sm text-gray-600">
                {row.data.specialty} — {row.data.reason}
              </p>
            )}
            {row.type === 'lab_order' && (
              <div className="mt-1 flex flex-wrap gap-1">
                {(row.data.tests || []).map((test) => (
                  <span key={test} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs">
                    {test}
                  </span>
                ))}
              </div>
            )}
            {row.type === 'radiology_request' && (
              <p className="text-sm text-gray-600">
                {row.data.investigation} · {row.data.body_part}
              </p>
            )}
            <div className="mt-3">
              <button
                type="button"
                className="w-full rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white"
                onClick={() => downloadPdf(row)}
              >
                Download PDF
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function DoctorReviewScreen() {
  const { id, noteId } = useParams();
  const navigate = useNavigate();
  const closePath = noteId ? ROUTES.DOCTOR_NOTES : doctorPatientPath(id);

  return (
    <ReviewModal forceEnglish onClose={() => navigate(closePath)}>
      <DoctorReviewBody />
    </ReviewModal>
  );
}

function DoctorReviewBody() {
  const { id, noteId: routeNoteId } = useParams();
  const noteId = routeNoteId;
  const navigate = useNavigate();
  const floatingSlot = useContext(ReviewModalFloatingSlotContext);
  const { draftNote, setDraftNote, showToast, refreshDoctor, doctor, promptFeedbackIfNeeded } =
    useDoctorSession();
  const [noteJson, setNoteJson] = useState(null);
  const [narrative, setNarrative] = useState('');
  const [medications, setMedications] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentsDetected, setDocumentsDetected] = useState({});
  const [noteType, setNoteType] = useState('first_visit');
  const [saving, setSaving] = useState(false);
  const [micBusy, setMicBusy] = useState(false);
  const [error, setError] = useState('');
  const [patientName, setPatientName] = useState('');
  const [loaded, setLoaded] = useState(false);
  const readOnly = Boolean(noteId);
  const noteJsonRef = useRef(noteJson);
  const noteTypeRef = useRef(noteType);
  const micBusyRef = useRef(micBusy);
  noteJsonRef.current = noteJson;
  noteTypeRef.current = noteType;
  micBusyRef.current = micBusy;

  const closePath = noteId ? ROUTES.DOCTOR_NOTES : doctorPatientPath(id);

  useEffect(() => {
    if (noteId) {
      doctorGetNote(noteId)
        .then((n) => {
          setNoteJson(n.note_json);
          setNarrative(n.narrative_text || '');
          setNoteType(n.note_type);
          setMedications(n.medications || []);
          setDocuments(n.documents || []);
          setPatientName(n.patient_name || '');
          setLoaded(true);
        })
        .catch((err) => setError(err.message));
      return;
    }
    if (!draftNote?.noteJson || draftNote.patientId !== id) {
      navigate(doctorPatientPath(id), { replace: true });
      return;
    }
    setNoteJson(draftNote.noteJson);
    setNarrative(draftNote.narrativeText || '');
    setNoteType(draftNote.noteType || 'first_visit');
    setMedications(draftNote.medications || []);
    setDocumentsDetected(draftNote.documentsDetected || {});
    setPatientName(draftNote.patient?.full_name || '');
    setLoaded(true);
  }, [draftNote, id, navigate, noteId]);

  const soap = noteJson?.soap || {};
  const subjective = soap.subjective || {};
  const objective = soap.objective || {};
  const assessment = soap.assessment || {};
  const plan = soap.plan || {};
  const isSoapNote = noteType === 'first_visit' || noteType === 'follow_up';

  const planMeds = (plan.medications_prescribed || plan.medication_changes || medications || []).map(
    toMedCard
  );
  const currentMeds = (subjective.current_medications || []).map(toMedCard);
  const givenMeds = (noteJson?.medications_given || medications || []).map(toMedCard);

  const update = (path, value) => setNoteJson((prev) => setPath(prev, path, value));

  const updateMedList = (path, nextMeds) => {
    const cleaned = nextMeds.map(fromMedCard);
    setMedications(cleaned);
    setNoteJson((prev) => setPath(prev, path, cleaned));
  };

  const handleMicTranscript = useCallback(
    async (transcript) => {
      const trimmed = String(transcript || '').trim();
      if (!trimmed || readOnly || micBusyRef.current) return;

      setMicBusy(true);
      setError('');
      try {
        const currentNote = noteJsonRef.current;
        const currentType = noteTypeRef.current;
        const allergies =
          currentNote?.soap?.subjective?.allergies ||
          draftNote?.patient?.allergies ||
          '';
        const updated = await updateDoctorNoteFromCorrection(currentNote, trimmed, {
          noteType: currentType,
          allergies,
        });
        setNoteJson(updated.noteJson);
        setNarrative(updated.narrativeText || '');
        setMedications(updated.medications || []);
        setNoteType(updated.noteType || currentType);
        setDraftNote((prev) =>
          prev
            ? {
                ...prev,
                noteJson: updated.noteJson,
                narrativeText: updated.narrativeText,
                medications: updated.medications,
                noteType: updated.noteType || prev.noteType,
                transcript: [prev.transcript, trimmed].filter(Boolean).join('\n\n'),
              }
            : prev
        );
      } catch (err) {
        setError(err.message || 'Failed to update note');
      } finally {
        setMicBusy(false);
      }
    },
    [draftNote?.patient?.allergies, readOnly, setDraftNote]
  );

  const handleApprove = async () => {
    setSaving(true);
    setError('');
    try {
      const created = await doctorCreateNote({
        patient_id: id,
        note_type: noteType,
        note_format: 'soap',
        recording_mode: 'push_to_talk',
        raw_transcript: draftNote?.transcript,
        note_json: noteJson,
        narrative_text: narrative,
        medications,
        generated_documents: (draftNote?.generatedDocuments || []).map((d) => ({
          document_type: d.document_type,
          document_json: d.document_json,
        })),
        audio_duration_seconds: draftNote?.audioDurationSeconds,
        generation_time_ms: draftNote?.generationTimeMs,
        status: 'approved',
        was_edited: true,
      });
      setDraftNote(null);
      await refreshDoctor();
      showToast('Note saved');
      promptFeedbackIfNeeded(created?.approved_notes_count);
      navigate(doctorPatientPath(id), { replace: true });
    } catch (err) {
      if (err.status === 402) {
        navigate(ROUTES.DOCTOR_PAYWALL);
        return;
      }
      setError(err.message || 'Approve failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    const confirmed = window.confirm('Are you sure you want to discard this note?');
    if (!confirmed) return;
    setDraftNote(null);
    navigate(closePath, { replace: true });
  };

  if (!loaded && !error) {
    return <div className="flex flex-1 items-center justify-center text-sm text-gray-400">…</div>;
  }

  const displayName = patientName || 'Clinical note';
  const avatarLetters = patientInitials(displayName);

  return (
    <>
      <div className={`flex h-full min-h-0 flex-col overflow-hidden ${REVIEW_PAGE_BG}`}>
        <div className="shrink-0 rounded-t-[24px] border-b border-gray-200 bg-white px-4 py-4 pr-16">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-light text-sm font-bold text-primary">
              {avatarLetters}
            </div>
            <div className="min-w-0">
              <p className="truncate text-lg font-bold text-gray-900">{displayName}</p>
              <p className="mt-0.5 text-xs font-medium text-gray-500">
                {NOTE_TYPE_LABELS[noteType] || noteType}
              </p>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-2.5">
          {error && (
            <div className="mb-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {error}
            </div>
          )}

          {micBusy ? (
            <NoteApplyingSkeleton />
          ) : (
          <div className="flex flex-col gap-2.5">
            {isSoapNote ? (
              <>
                <SoapGroupCard letter="S" title="Subjective">
                  {noteType === 'first_visit' ? (
                    <>
                      <GrayField
                        label="Chief complaint"
                        value={subjective.chief_complaint}
                        readOnly={readOnly}
                        onChange={(v) => update('soap.subjective.chief_complaint', v)}
                        rows={2}
                      />
                      <GrayField
                        label="History of Present Illness"
                        value={subjective.history_of_present_illness}
                        readOnly={readOnly}
                        onChange={(v) => update('soap.subjective.history_of_present_illness', v)}
                        rows={4}
                      />
                      <GrayField
                        label="Past Medical History"
                        value={subjective.past_medical_history}
                        readOnly={readOnly}
                        onChange={(v) => update('soap.subjective.past_medical_history', v)}
                      />
                      <GrayField
                        label="Family history"
                        value={subjective.family_history}
                        hideIfEmpty
                        readOnly={readOnly}
                        onChange={(v) => update('soap.subjective.family_history', v)}
                      />
                      <GrayField
                        label="Social history"
                        value={subjective.social_history}
                        hideIfEmpty
                        readOnly={readOnly}
                        onChange={(v) => update('soap.subjective.social_history', v)}
                      />
                      <GrayField
                        label="Allergies"
                        value={subjective.allergies || (readOnly ? 'None' : '')}
                        danger={Boolean(subjective.allergies)}
                        readOnly={readOnly}
                        onChange={(v) => update('soap.subjective.allergies', v)}
                      />
                    </>
                  ) : (
                    <>
                      <GrayField
                        label="Current complaints"
                        value={subjective.current_complaints}
                        readOnly={readOnly}
                        onChange={(v) => update('soap.subjective.current_complaints', v)}
                        rows={3}
                      />
                      <GrayField
                        label="Condition status"
                        value={subjective.condition_status}
                        readOnly={readOnly}
                        onChange={(v) => update('soap.subjective.condition_status', v)}
                        rows={1}
                      />
                      <GrayField
                        label="Medication compliance"
                        value={subjective.medication_compliance}
                        readOnly={readOnly}
                        onChange={(v) => update('soap.subjective.medication_compliance', v)}
                      />
                    </>
                  )}
                  <MedList
                    meds={currentMeds}
                    readOnly={readOnly}
                    onChangeAt={(index, next) => {
                      const list = [...currentMeds];
                      list[index] = next;
                      update('soap.subjective.current_medications', list.map(fromMedCard));
                    }}
                    onDeleteAt={(index) => {
                      update(
                        'soap.subjective.current_medications',
                        currentMeds.filter((_, i) => i !== index).map(fromMedCard)
                      );
                    }}
                  />
                </SoapGroupCard>

                <SoapGroupCard letter="O" title="Objective">
                  {hasVitals(objective.vitals) && (
                    <VitalsCardGrid
                      note={doctorVitalsToNurseNote(objective.vitals)}
                      allowDelete={!readOnly}
                      onChange={(vitalsNote) =>
                        update('soap.objective.vitals', nurseNoteToDoctorVitals(vitalsNote))
                      }
                    />
                  )}
                  <GrayField
                    label="Examination"
                    value={objective.examination_findings}
                    hideIfEmpty={noteType === 'follow_up'}
                    readOnly={readOnly}
                    onChange={(v) => update('soap.objective.examination_findings', v)}
                    rows={3}
                  />
                </SoapGroupCard>

                <SoapGroupCard letter="A" title="Assessment">
                  <GrayField
                    label="Diagnosis"
                    value={assessment.diagnosis}
                    readOnly={readOnly}
                    onChange={(v) => update('soap.assessment.diagnosis', v)}
                    rows={2}
                  />
                  {noteType === 'follow_up' && (
                    <GrayField
                      label="Progress"
                      value={assessment.progress}
                      readOnly={readOnly}
                      onChange={(v) => update('soap.assessment.progress', v)}
                    />
                  )}
                </SoapGroupCard>

                <SoapGroupCard letter="P" title="Plan">
                  <MedList
                    meds={planMeds}
                    readOnly={readOnly}
                    onChangeAt={(index, next) => {
                      const list = [...planMeds];
                      list[index] = next;
                      updateMedList(
                        noteType === 'follow_up'
                          ? 'soap.plan.medication_changes'
                          : 'soap.plan.medications_prescribed',
                        list
                      );
                    }}
                    onDeleteAt={(index) => {
                      updateMedList(
                        noteType === 'follow_up'
                          ? 'soap.plan.medication_changes'
                          : 'soap.plan.medications_prescribed',
                        planMeds.filter((_, i) => i !== index)
                      );
                    }}
                  />
                  {noteType === 'follow_up' && (
                    <GrayField
                      label="Investigations"
                      value={plan.investigations}
                      hideIfEmpty
                      readOnly={readOnly}
                      onChange={(v) => update('soap.plan.investigations', v)}
                    />
                  )}
                  <GrayField
                    label="Follow-up"
                    value={plan.follow_up}
                    readOnly={readOnly}
                    onChange={(v) => update('soap.plan.follow_up', v)}
                  />
                  <GrayField
                    label="Instructions"
                    value={plan.instructions}
                    readOnly={readOnly}
                    onChange={(v) => update('soap.plan.instructions', v)}
                    rows={3}
                  />
                </SoapGroupCard>
              </>
            ) : noteType === 'procedure' ? (
              <>
                <SoapGroupCard letter="1" title="Indication">
                  <GrayField
                    value={noteJson?.indication}
                    readOnly={readOnly}
                    onChange={(v) => update('indication', v)}
                    rows={3}
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="2" title="Procedure">
                  <GrayField
                    value={noteJson?.procedure_performed}
                    readOnly={readOnly}
                    onChange={(v) => update('procedure_performed', v)}
                    rows={3}
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="3" title="Anesthesia" hide={!noteJson?.anesthesia_used && readOnly}>
                  <GrayField
                    value={noteJson?.anesthesia_used}
                    readOnly={readOnly}
                    onChange={(v) => update('anesthesia_used', v)}
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="4" title="Findings" hide={!noteJson?.findings && readOnly}>
                  <GrayField
                    value={noteJson?.findings}
                    readOnly={readOnly}
                    onChange={(v) => update('findings', v)}
                    rows={3}
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="5" title="Complications">
                  <GrayField
                    value={noteJson?.complications || (readOnly ? 'None' : '')}
                    danger={Boolean(noteJson?.complications)}
                    readOnly={readOnly}
                    onChange={(v) => update('complications', v)}
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="6" title="Medications Given" hide={!givenMeds.length}>
                  <MedList
                    meds={givenMeds}
                    readOnly={readOnly}
                    onChangeAt={(index, next) => {
                      const list = [...givenMeds];
                      list[index] = next;
                      updateMedList('medications_given', list);
                    }}
                    onDeleteAt={(index) =>
                      updateMedList(
                        'medications_given',
                        givenMeds.filter((_, i) => i !== index)
                      )
                    }
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="7" title="Post Instructions">
                  <GrayField
                    value={noteJson?.post_procedure_instructions}
                    readOnly={readOnly}
                    onChange={(v) => update('post_procedure_instructions', v)}
                    rows={3}
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="8" title="Follow-up">
                  <GrayField
                    value={noteJson?.follow_up}
                    readOnly={readOnly}
                    onChange={(v) => update('follow_up', v)}
                  />
                </SoapGroupCard>
              </>
            ) : (
              <>
                <SoapGroupCard letter="1" title="Chief Complaint">
                  <GrayField
                    value={noteJson?.chief_complaint}
                    readOnly={readOnly}
                    onChange={(v) => update('chief_complaint', v)}
                    rows={2}
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="2" title="Onset and Severity">
                  <GrayField
                    label="Onset"
                    value={noteJson?.onset}
                    readOnly={readOnly}
                    onChange={(v) => update('onset', v)}
                  />
                  <GrayField
                    label="Severity"
                    value={noteJson?.severity}
                    readOnly={readOnly}
                    onChange={(v) => update('severity', v)}
                    rows={1}
                  />
                </SoapGroupCard>
                <SoapGroupCard
                  letter="3"
                  title="Symptoms"
                  hide={!noteJson?.associated_symptoms && readOnly}
                >
                  <GrayField
                    value={noteJson?.associated_symptoms}
                    readOnly={readOnly}
                    onChange={(v) => update('associated_symptoms', v)}
                    rows={3}
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="4" title="Vitals" hide={!hasVitals(noteJson?.vitals)}>
                  <VitalsCardGrid
                    note={doctorVitalsToNurseNote(noteJson?.vitals)}
                    allowDelete={!readOnly}
                    onChange={(vitalsNote) =>
                      setNoteJson((prev) => ({
                        ...prev,
                        vitals: nurseNoteToDoctorVitals(vitalsNote),
                      }))
                    }
                  />
                </SoapGroupCard>
                <SoapGroupCard
                  letter="5"
                  title="Examination"
                  hide={!noteJson?.examination_findings && readOnly}
                >
                  <GrayField
                    value={noteJson?.examination_findings}
                    readOnly={readOnly}
                    onChange={(v) => update('examination_findings', v)}
                    rows={3}
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="6" title="Diagnosis">
                  <GrayField
                    value={noteJson?.diagnosis}
                    readOnly={readOnly}
                    onChange={(v) => update('diagnosis', v)}
                    rows={2}
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="7" title="Immediate Treatment">
                  <GrayField
                    value={noteJson?.immediate_treatment}
                    readOnly={readOnly}
                    onChange={(v) => update('immediate_treatment', v)}
                    rows={3}
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="8" title="Medications Given" hide={!givenMeds.length}>
                  <MedList
                    meds={givenMeds}
                    readOnly={readOnly}
                    onChangeAt={(index, next) => {
                      const list = [...givenMeds];
                      list[index] = next;
                      updateMedList('medications_given', list);
                    }}
                    onDeleteAt={(index) =>
                      updateMedList(
                        'medications_given',
                        givenMeds.filter((_, i) => i !== index)
                      )
                    }
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="9" title="Disposition">
                  <GrayField
                    value={noteJson?.disposition}
                    readOnly={readOnly}
                    onChange={(v) => update('disposition', v)}
                    rows={1}
                  />
                </SoapGroupCard>
                <SoapGroupCard letter="10" title="Referral" hide={!noteJson?.referral && readOnly}>
                  <GrayField
                    value={noteJson?.referral}
                    readOnly={readOnly}
                    onChange={(v) => update('referral', v)}
                  />
                </SoapGroupCard>
              </>
            )}

            <DocumentsSection
              documents={documents}
              documentsDetected={documentsDetected}
              patientName={patientName}
              doctorName={doctor?.full_name || ''}
              clinicName={doctor?.clinic_name || ''}
              clinicLogoUrl={doctor?.clinic_logo_url || ''}
            />
          </div>
          )}
        </div>

        {!readOnly && (
          <ActionBar
            fixed={false}
            onSave={saving || micBusy ? undefined : handleApprove}
            onDiscard={handleDiscard}
            saveDisabled={saving || micBusy}
          />
        )}
      </div>

        {!readOnly && floatingSlot
          ? createPortal(
              <HoldToRecordButton
                patientId={id || null}
                size="fab"
                anchor="inline"
                holdOptions={{
                  onTranscript: handleMicTranscript,
                  patientLabel: patientName || null,
                }}
              />,
              floatingSlot
            )
          : null}
    </>
  );
}
